import { getNodeName, isNode } from '@floating-ui/utils/dom'
import type {
  FloatingElement,
  MaybeElement,
  ReferenceElement,
  UseFloatingOptions,
} from '@floating-ui/vue'
import { autoUpdate, useFloating } from '@floating-ui/vue'
import {
  ref,
  type Ref,
  unref,
  type ComponentPublicInstance,
  watch,
  onBeforeUnmount,
} from 'vue'
import { useEventListener, Fn } from '@vueuse/core'

import { MaybeRef } from '@/types/types'

export interface UsePopoverOptions<
  ReferenceEl extends ReferenceElement = ReferenceElement,
> {
  /**
   * The reference (or anchor) element that is being referred to for positioning.
   */
  referenceRef?: Readonly<Ref<MaybeElement<ReferenceEl>>>

  /**
   * The floating element that is being positioned relative to the reference element.
   */
  floatingRef?: Readonly<Ref<MaybeElement<FloatingElement>>>

  /**
   * Represents the open/closed state of the popover.
   */
  isOpen?: UseFloatingOptions['open']

  /**
   * The placement of the popover relative to the reference element.
   *
   * @default 'buttom'
   */
  placement?: UseFloatingOptions['placement']

  /**
   * These are plain objects that modify the positioning coordinates in some fashion, or
   * provide useful data for the consumer to use.
   */
  middleware?: UseFloatingOptions['middleware']

  /**
   * The type of CSS position property to use.
   *
   * @default 'absolute'
   */
  strategy?: UseFloatingOptions['strategy']
}

/**
 *
 */
export function usePopover<
  ReferenceEl extends ReferenceElement = ReferenceElement,
>(options: UsePopoverOptions<ReferenceEl>) {
  const {
    referenceRef = ref<ReferenceEl>(),
    floatingRef = ref<FloatingElement>(),
    isOpen = false,
    middleware,
    placement: initialPlacement = 'bottom',
    strategy: initialStrategy = 'absolute',
  } = options

  const useFloatingReturn = useFloating<ReferenceEl>(
    referenceRef,
    floatingRef,
    {
      strategy: initialStrategy,
      placement: initialPlacement,
      open: isOpen,
      middleware,
      whileElementsMounted: autoUpdate,
    },
  )

  return {
    referenceRef,
    floatingRef,
    ...useFloatingReturn,
  }
}

export type UsePopoverReturn = ReturnType<typeof usePopover>

export interface UseHoverOptions {
  /**
   * Waits for the specified time before closing the popover.
   */
  closeDelay?: MaybeRef<number>
  /**
   * Waits for the specified time before opening the popover.
   */
  openDelay?: MaybeRef<number>
  /**
   * The `isOpen` ref that is also passed to `usePopover` to control
   * the open state.
   */
  isOpen: Ref<boolean>
}

/**
 * Opens the floating element while hovering over the reference element, like CSS :hover.
 *
 * Includes the ability to enter the floating element without it closing.
 *
 * This composable returns event handlers for the reference and floating elements
 * of `usePopover` which can be assigned using `v-on`.
 */
export function useHover(options: UseHoverOptions) {
  const { openDelay = 0, closeDelay = 0, isOpen } = options
  const pointerType = ref()
  const timeout = ref(-1)

  function closeWithDelay() {
    const delay = unref(closeDelay)
    if (delay) {
      clearTimeout(timeout.value)
      timeout.value = window.setTimeout(() => (isOpen.value = false), delay)
    } else {
      isOpen.value = false
    }
  }

  return {
    reference: {
      pointerenter(event: PointerEvent) {
        pointerType.value = event.pointerType
      },
      pointerdown(event: PointerEvent) {
        pointerType.value = event.pointerType
      },
      mouseenter() {
        clearTimeout(timeout.value)
        const delay = unref(openDelay)
        if (delay) {
          timeout.value = window.setTimeout(() => {
            isOpen.value = true
          }, delay)
        } else {
          isOpen.value = true
        }
      },
      mouseleave() {
        const shouldClose = pointerType.value !== 'touch'
        if (shouldClose) {
          closeWithDelay()
        }
      },
    },
    floating: {
      mouseenter() {
        clearTimeout(timeout.value)
      },
      mouseleave() {
        closeWithDelay()
      },
    },
  }
}

export interface UseDismissOptions {
  referenceRef: UsePopoverReturn['referenceRef']
  floatingRef: UsePopoverReturn['floatingRef']

  /**
   * The `isOpen` ref that is also passed to `usePopover` to control
   * the open state.
   */
  isOpen: Ref<boolean>
}

/**
 * Closes the floating element when a dismissal is requested — by default, when the user
 * presses the escape key or clicks outside of the floating element with their pointer.
 */
export function useDismiss(options: UseDismissOptions) {
  const { floatingRef, referenceRef, isOpen } = options

  function closeOnPressOutside(event: MouseEvent) {
    if (!isOpen.value) {
      return
    }

    const unwrappedFloatingRef = unwrapElement(unref(floatingRef))
    const unwrappedReferenceRef = unwrapElement(unref(referenceRef))
    if (
      isEventTargetWithin(event, unwrappedFloatingRef) ||
      (unwrappedReferenceRef instanceof Node &&
        isEventTargetWithin(event, unwrappedReferenceRef))
    ) {
      return
    }

    isOpen.value = false
  }

  function closeOnEscapeKeyDown(event: KeyboardEvent) {
    if (!isOpen.value || event.key !== 'Escape') {
      return
    }
    event.stopImmediatePropagation()

    isOpen.value = false
  }

  const stopListeners: Fn[] = []
  watch(isOpen, (newIsOpen) => {
    if (newIsOpen) {
      stopListeners.push(
        useEventListener(document, 'keydown', closeOnEscapeKeyDown, {
          capture: true,
        }),
      )

      stopListeners.push(
        useEventListener(document, 'pointerdown', closeOnPressOutside),
      )
    } else {
      stopListeners.forEach((stop) => stop())
      stopListeners.length = 0
    }
  })

  onBeforeUnmount(() => {
    stopListeners.forEach((stop) => stop())
    stopListeners.length = 0
  })
}

export function isEventTargetWithin(
  event: Event,
  node: Node | null | undefined,
) {
  if (node == null) {
    return false
  }

  if ('composedPath' in event) {
    return event.composedPath().includes(node)
  }

  // TS thinks `event` is of type never as it assumes all browsers support composedPath, but browsers without shadow dom don't
  const e = event as Event
  return e.target != null && node.contains(e.target as Node)
}

function isComponentPublicInstance(
  target: unknown,
): target is ComponentPublicInstance {
  return target != null && typeof target === 'object' && '$el' in target
}

export function unwrapElement<T>(target: MaybeElement<T>) {
  if (isComponentPublicInstance(target)) {
    const element = target.$el as Exclude<
      MaybeElement<T>,
      ComponentPublicInstance
    >

    return isNode(element) && getNodeName(element) === '#comment'
      ? null
      : element
  }

  return target as Exclude<MaybeElement<T>, ComponentPublicInstance>
}
